/**
 * Trading Reports Section Component
 * Main section for managing trading report preferences in Settings page
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import {
  getReportPreferences,
  updateReportPreferences,
  sendTestReport,
  type ReportPreferences,
  type UpdateReportPreferencesRequest,
} from '../../services/tradingReportsApi';
import { UserAwareCacheKeys } from '../../utils/userAwareCacheKeys';
import { useUserContext } from '../../hooks/useUserContext';
import { TelegramLinking } from './TelegramLinking';
import { ReportHistoryComponent } from './ReportHistory';

export const TradingReportsSection: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const { userId } = useUserContext();
  const queryClient = useQueryClient();

  // Fetch report preferences
  const { data: preferences, isLoading } = useQuery<ReportPreferences>({
    queryKey: userId ? UserAwareCacheKeys.reportPreferences(userId) : ['disabled'],
    queryFn: getReportPreferences,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Check feature flag access for each report type
  const { isEnabled: canUseDailyReports } = useFeatureFlag('trading_report_daily');
  const { isEnabled: canUseWeeklyReports } = useFeatureFlag('trading_report_weekly');
  const { isEnabled: canUseTelegram } = useFeatureFlag('trading_report_telegram');

  // Update preferences mutation
  const updateMutation = useMutation<ReportPreferences, Error, UpdateReportPreferencesRequest>({
    mutationFn: updateReportPreferences,
    onSuccess: (data) => {
      console.log('🔄 Mutation success, received data:', data);
      if (userId) {
        queryClient.setQueryData(UserAwareCacheKeys.reportPreferences(userId), data);
        console.log('✅ Updated cache with:', queryClient.getQueryData(UserAwareCacheKeys.reportPreferences(userId)));
      }
      toast.success('Report preferences updated successfully');
    },
    onError: (error) => {
      console.error('❌ Mutation error:', error);
      toast.error(`Failed to update preferences: ${error.message || 'Unknown error'}`);
    },
  });

  // Send test report mutation
  const testReportMutation = useMutation<any, Error, 'daily' | 'weekly' | 'monthly'>({
    mutationFn: sendTestReport,
    onSuccess: (data) => {
      // Show a longer duration toast for background processing message
      toast.success(data.message || 'Test report queued successfully', {
        duration: 6000, // 6 seconds to give time to read the message
      });
      if (userId) {
        // Refresh history after a delay to allow background task to complete
        setTimeout(() => {
          queryClient.invalidateQueries({ 
            queryKey: UserAwareCacheKeys.reportHistory(userId) 
          });
        }, 3000); // Refresh after 3 seconds
      }
    },
    onError: (error: any) => {
      // Handle rate limit error specifically
      if (error.response?.status === 429) {
        toast.error('Please wait before sending another test report (5 minute cooldown)');
      } else if (error.response?.status === 504) {
        toast.error('Request timed out. Database might be slow. Please try again.');
      } else {
        toast.error(`Failed to send test report: ${error.message || 'Unknown error'}`);
      }
    },
  });

  const handleTogglePreference = (key: keyof UpdateReportPreferencesRequest) => {
    if (!preferences) return;
    
    const currentValue = preferences[key as keyof ReportPreferences];
    if (typeof currentValue === 'boolean') {
      updateMutation.mutate({ [key]: !currentValue });
    }
  };

  const handleUpdateSendHour = (hour: number) => {
    updateMutation.mutate({ 
      daily_send_hour: hour,
      weekly_send_hour: hour,
      monthly_send_hour: hour
    });
  };

  const handleUpdateTopStrategies = (count: number) => {
    updateMutation.mutate({ top_strategies_count: count });
  };

  const handleSendTestReport = (type: 'daily' | 'weekly' | 'monthly') => {
    if (!preferences?.email_enabled && !preferences?.telegram_enabled) {
      toast.error('Please enable at least one delivery channel (Email or Telegram)');
      return;
    }
    testReportMutation.mutate(type);
  };

  const handleLinkSuccess = () => {
    if (userId) {
      queryClient.invalidateQueries({ 
        queryKey: UserAwareCacheKeys.reportPreferences(userId) 
      });
    }
  };

  if (isLoading) {
    return (
      <motion.section
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeInOut' } }
        }}
        className="bg-white/10 backdrop-blur-lg border border-white/20 p-6 rounded-xl shadow-xl"
      >
        <div className="space-y-4">
          <div className="h-6 bg-white/10 rounded animate-pulse" />
          <div className="h-4 bg-white/10 rounded animate-pulse w-3/4" />
          <div className="h-4 bg-white/10 rounded animate-pulse w-1/2" />
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeInOut' } }
      }}
      className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-xl overflow-hidden"
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="h-6 w-6 text-blue-400" />
          <div>
            <h3 className="text-xl font-semibold text-white/90">Trading Reports</h3>
            <p className="text-sm text-white/60 mt-1">
              Receive automated performance summaries via email and Telegram
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-white/60" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-white/60" />
        )}
      </button>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && preferences && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-white/20"
          >
            <div className="p-6 space-y-6">
              {/* Report Frequency Settings */}
              <div>
                <h4 className="text-sm font-medium text-white/90 mb-4">Report Frequency</h4>
                <div className="space-y-3">
                  {/* Daily Reports */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-white/90">Daily Reports</span>
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-400/30">
                          Elite Only
                        </span>
                      </div>
                      <p className="text-xs text-white/60 mt-1">
                        End of day trading summary
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('daily_enabled')}
                      disabled={updateMutation.isPending || !canUseDailyReports}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        preferences.daily_enabled ? 'bg-blue-500' : 'bg-white/20'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                          preferences.daily_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Weekly Reports */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-white/90">Weekly Reports</span>
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-400/30">
                          Standard+
                        </span>
                      </div>
                      <p className="text-xs text-white/60 mt-1">
                        Monday morning summary
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('weekly_enabled')}
                      disabled={updateMutation.isPending || !canUseWeeklyReports}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        preferences.weekly_enabled ? 'bg-blue-500' : 'bg-white/20'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                          preferences.weekly_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Monthly Reports */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-white/90">Monthly Reports</span>
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-300 text-xs rounded-full border border-green-400/30">
                          All Plans
                        </span>
                      </div>
                      <p className="text-xs text-white/60 mt-1">
                        First of month summary
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('monthly_enabled')}
                      disabled={updateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-centers rounded-full transition-colors duration-200 ${
                        preferences.monthly_enabled ? 'bg-blue-500' : 'bg-white/20'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                          preferences.monthly_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Delivery Channels */}
              <div>
                <h4 className="text-sm font-medium text-white/90 mb-4">Delivery Channels</h4>
                <div className="space-y-3">
                  {/* Email Toggle */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <span className="text-white/90">Email Delivery</span>
                      <p className="text-xs text-white/60 mt-1">
                        Receive detailed reports via email
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('email_enabled')}
                      disabled={updateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        preferences.email_enabled ? 'bg-blue-500' : 'bg-white/20'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                          preferences.email_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Telegram Toggle */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-white/90">Telegram Delivery</span>
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-400/30">
                          Standard+
                        </span>
                      </div>
                      <p className="text-xs text-white/60 mt-1">
                        Receive concise reports via Telegram bot
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('telegram_enabled')}
                      disabled={updateMutation.isPending || !preferences.telegram_chat_id || !canUseTelegram}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        preferences.telegram_enabled ? 'bg-blue-500' : 'bg-white/20'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                          preferences.telegram_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Telegram Linking */}
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <TelegramLinking
                      isLinked={!!preferences.telegram_chat_id}
                      telegramUsername={preferences.telegram_username}
                      linkedAt={preferences.telegram_linked_at}
                      onLinkSuccess={handleLinkSuccess}
                    />
                  </div>
                </div>
              </div>

              {/* Report Settings */}
              <div>
                <h4 className="text-sm font-medium text-white/90 mb-4">Report Settings</h4>
                <div className="space-y-4">
                  {/* Report Send Time */}
                  {(preferences.daily_enabled || preferences.weekly_enabled || preferences.monthly_enabled) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <label className="text-sm text-white/70 mb-2 block">
                        Report Send Time (Hour in your timezone)
                      </label>
                      <p className="text-xs text-white/50 mb-2">
                        This time will be used for all enabled reports (daily, weekly, monthly)
                      </p>
                      <select
                        value={preferences.daily_send_hour}
                        onChange={(e) => handleUpdateSendHour(parseInt(e.target.value))}
                        disabled={updateMutation.isPending}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50"
                        style={{ colorScheme: 'dark' }}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
                          const period = i < 12 ? 'AM' : 'PM';
                          return (
                            <option key={i} value={i} style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                              {hour12}:00 {period}
                            </option>
                          );
                        })}
                      </select>
                    </motion.div>
                  )}

                  {/* Top Strategies Count */}
                  <div>
                    <label className="text-sm text-white/70 mb-2 block">
                      Number of Top Strategies to Include
                    </label>
                    <select
                      value={preferences.top_strategies_count}
                      onChange={(e) => handleUpdateTopStrategies(parseInt(e.target.value))}
                      disabled={updateMutation.isPending}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50"
                      style={{ colorScheme: 'dark' }}
                    >
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                        <option key={num} value={num} style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                          {num} {num === 1 ? 'Strategy' : 'Strategies'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Test Report Button */}
              <div className="pt-4 border-t border-white/20">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white/90">Test Reports</h4>
                  <p className="text-xs text-white/50">5 minute cooldown between tests</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleSendTestReport('daily')}
                    disabled={testReportMutation.isPending}
                    className="flex-1 px-4 py-2 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                  >
                    <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                    Test Daily
                  </button>
                  <button
                    onClick={() => handleSendTestReport('weekly')}
                    disabled={testReportMutation.isPending}
                    className="flex-1 px-4 py-2 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                  >
                    <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                    Test Weekly
                  </button>
                  <button
                    onClick={() => handleSendTestReport('monthly')}
                    disabled={testReportMutation.isPending}
                    className="flex-1 px-4 py-2 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                  >
                    <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                    Test Monthly
                  </button>
                </div>
              </div>

              {/* Report History */}
              <div className="pt-4 border-t border-white/20">
                <button
                  onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                  className="w-full flex items-center justify-between mb-4"
                >
                  <h4 className="text-sm font-medium text-white/90">Report History</h4>
                  {isHistoryExpanded ? (
                    <ChevronUpIcon className="w-4 h-4 text-white/60" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-white/60" />
                  )}
                </button>
                <AnimatePresence>
                  {isHistoryExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ReportHistoryComponent />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};
